// Disclosure + Accordion — expand/collapse sections on the React Aria
// foundation (ADR-029), per COMPONENTS §Catalog. <Disclosure> is one section
// (trigger row + panel); <Accordion> groups several with a shared border
// frame. Multiple sections may be open at once (the SOTA default); pass
// `exclusive` for the classic one-at-a-time accordion.
//
// Production variations built in: optional `description` under the title,
// optional `trailing` adornment on the trigger row (a count Badge —
// "Alarms (3)"), `disabled`, `defaultExpanded`. The caret rotates for state
// (plus aria-expanded). Motion is a calm reveal-on-open (content fades + rises
// in via o-reveal-in) — NOT a height animation: React Aria sets `hidden`
// (display:none) on a collapsed panel, so a height transition would mean
// removing `hidden` and leaking collapsed content to AT / find-in-page. Collapse
// closes instantly; reduced-motion neutralizes the reveal (global index.css).

import type { ReactNode } from 'react';
import {
  DisclosureGroup,
  Disclosure as AriaDisclosure,
  DisclosurePanel,
  Button as AriaButton,
  Heading,
} from 'react-aria-components';
import { icons } from './icons.js';

export interface DisclosureProps {
  /** The trigger row's title. */
  title: ReactNode;
  /** Leading adornment (an icon) — rendered as a flex item BEFORE the title. Keep
   *  icons out of `title`: an icon inside the title's text line box adds a baseline
   *  strut that inflates the trigger height. */
  leading?: ReactNode;
  /** Smaller line under the title. */
  description?: string;
  /** Trailing adornment on the trigger row (count Badge, status). Decorative. */
  trailing?: ReactNode;
  defaultExpanded?: boolean;
  /** CONTROLLED expansion (the files shelf: closed when media exists, open when
   *  none, opened by a documents filter — the consumer owns the rule). Pair with
   *  `onExpandedChange`; omit both for the uncontrolled default. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  disabled?: boolean;
  /** Edge-to-edge trigger hover (drops the rounded inset) for use inside a divided
   *  Accordion / card — the hover fills the row and aligns with full-width dividers. */
  flush?: boolean;
  /** A control on the trigger ROW that is not the trigger (the shelf's "Add a
   *  document", a lane's "Open product ›") — rendered as a SIBLING of the
   *  trigger button, never inside it (a button cannot nest a button; the
   *  `trailing` adornment is decorative and aria-hidden by contract). */
  action?: ReactNode;
  children: ReactNode;
  /** Distinct id — required inside an Accordion (the group keys expansion by id). */
  id?: string;
  /** The trigger heading's level (default 3) — a section that sits directly under a page's h1 passes 2 so the outline stays honest (axe heading-order). */
  level?: 2 | 3 | 4 | 5;
  /** The panel's inner inset. `none` runs the content edge to edge (a divided
   *  list or a table inside the section — the files shelf); the consumer owns
   *  any inset it wants. Default keeps the text inset aligned under the title. */
  panelPadding?: 'default' | 'none';
  className?: string;
}

export function Disclosure({
  title,
  leading,
  description,
  trailing,
  defaultExpanded,
  expanded,
  onExpandedChange,
  disabled,
  flush = false,
  action,
  children,
  id,
  level = 3,
  panelPadding = 'default',
  className = '',
}: DisclosureProps) {
  return (
    <AriaDisclosure
      id={id}
      defaultExpanded={defaultExpanded}
      isExpanded={expanded}
      onExpandedChange={onExpandedChange}
      isDisabled={disabled}
      className={`group ${className}`}
    >
      {/* The heading wraps ONLY the trigger; the action sits beside it in the
          same row (outside the heading — it is not part of the section's name).
          The ROW carries the hover, so the trigger and its action light up as
          ONE line (a grey trigger beside a white action read as two controls). */}
      <div className={`flex items-center ${flush ? '' : 'rounded-md '}hover:bg-[var(--color-bg-hover)] transition-colors`}>
      <Heading level={level} className="m-0 min-w-0 flex-1">
        <AriaButton
          slot="trigger"
          className={
            'w-full min-h-[2.5rem] flex items-center gap-2.5 px-4 py-3 text-start cursor-pointer ' +
            // `flush` drops the rounded inset so the row fills a divided Accordion
            // edge-to-edge and lines up with the full-width divider.
            (flush ? '' : 'rounded-md ') +
            'text-[var(--color-text-primary)] data-[pressed]:bg-[var(--color-border)] transition-colors ' +
            'outline-none focus-ring ' +
            'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed'
          }
        >
          <icons.caretDown
            aria-hidden
            size={14}
            className="shrink-0 text-[var(--color-text-secondary)] transition-transform -rotate-90 rtl:rotate-90 group-data-[expanded]:rotate-0 group-data-[expanded]:rtl:rotate-0"
          />
          {leading && <span className="flex shrink-0 items-center">{leading}</span>}
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium truncate">{title}</span>
            {description && (
              <span className="block text-xs text-[var(--color-text-secondary)] truncate">
                {description}
              </span>
            )}
          </span>
          {trailing && (
            <span aria-hidden className="flex shrink-0 items-center">
              {trailing}
            </span>
          )}
        </AriaButton>
      </Heading>
      {action && <div className="flex shrink-0 items-center pe-3">{action}</div>}
      </div>
      {/* Padding lives on an INNER wrapper, never the panel itself: React Aria
          collapses DisclosurePanel with hidden="until-found" (content-visibility:hidden,
          so collapsed content stays find-in-page discoverable), which Tailwind's
          preflight intentionally does NOT set display:none. The panel's own box still
          lays out, so padding ON the panel would render a phantom ~16px when collapsed.
          Padding on a child inside the skipped subtree ⇒ a collapsed panel is truly
          zero-height, while find-in-page still works. */}
      <DisclosurePanel className="group-data-[expanded]:o-reveal-in">
        <div className={`${panelPadding === 'none' ? '' : 'px-4 pb-4 pt-3 ps-[2.4rem] '}text-sm text-[var(--color-text-primary)]`}>{children}</div>
      </DisclosurePanel>
    </AriaDisclosure>
  );
}

export interface AccordionProps {
  /** One-at-a-time mode (classic accordion). Default: multiple may be open. */
  exclusive?: boolean;
  /**
   * Initially-open section ids. Inside a group, the GROUP owns expansion —
   * a child's `defaultExpanded` is ignored (React Aria contract), so every
   * <Disclosure> in an Accordion needs an `id`.
   */
  defaultExpandedIds?: string[];
  /** <Disclosure> children — each with an `id`. */
  children: ReactNode;
  className?: string;
}

export function Accordion({
  exclusive = false,
  defaultExpandedIds,
  children,
  className = '',
}: AccordionProps) {
  return (
    <DisclosureGroup
      allowsMultipleExpanded={!exclusive}
      defaultExpandedKeys={defaultExpandedIds}
      className={`rounded-md border border-[var(--color-border)] divide-y divide-[var(--color-border)] ${className}`}
    >
      {children}
    </DisclosureGroup>
  );
}
